import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, calcItem, tablePrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);
const KEY = "sanvalle_cart";

const refreshed = (i, p) => {
  if (!i.option) return p.price_table ? null : { ...i, price: p.price, regular_price: p.regular_price };
  const tp = p.price_table ? tablePrice(p, i.option, i.quantity) : {};
  return tp.price ? { ...i, price: tp.price, regular_price: tp.regular } : null;
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => localStorage.setItem(KEY, JSON.stringify(items)), [items]);

  const { user } = useAuth();
  const role = user ? user.role : user;
  useEffect(() => {
    if (role === null) return;
    api.get("/products").then(({ data }) => {
      const byId = Object.fromEntries(data.map((p) => [p.id, p]));
      setItems((prev) => prev.map((i) => byId[i.product_id] && refreshed(i, byId[i.product_id])).filter(Boolean));
    }).catch(() => {});
  }, [role]);

  const add = (product, { width, height, quantity, option, price, regular_price }) =>
    setItems((prev) => [
      ...prev,
      {
        key: `${product.id}-${Date.now()}`,
        product_id: product.id,
        name: product.name,
        image_url: product.image_url,
        pricing_type: product.pricing_type,
        price: price ?? product.price,
        regular_price: option ? regular_price : product.regular_price,
        unit_label: product.unit_label,
        option: option || null,
        option_label: option ? product.price_table.option_label || "Opção" : null,
        width: product.pricing_type === "m2" ? Number(width) : null,
        height: product.pricing_type === "m2" ? Number(height) : null,
        quantity: Number(quantity),
      },
    ]);
  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const updateQty = (key, quantity) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, Number(quantity) || 1) } : i)));
  const clear = () => setItems([]);

  const lines = useMemo(() => items.map((i) => ({ ...i, ...calcItem(i.pricing_type, i.price, i) })), [items]);
  const total = lines.reduce((s, i) => s + i.subtotal, 0);

  return (
    <CartContext.Provider value={{ items: lines, add, remove, updateQty, clear, total, count: items.length }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
